<script setup lang="ts">
  import { storeToRefs } from 'pinia';

  import { type Call, useSipStore } from '@bv/stores';
  import { trans } from '@bv/utils';

  import BvActiveCall from './partials/ActiveCall.vue';
  import AudioVisualizer from './partials/AudioVisualizer.vue';
  import CollapseIcon from './partials/icons/collapse.svg';
  import ExpandIcon from './partials/icons/expand.svg';
  import BvPreviousCall from './partials/PreviousCall.vue';

  const sipStore = useSipStore();

  const { activeCall, previousCall } = storeToRefs(sipStore);

  withDefaults(
    defineProps<{
      showExpand?: boolean;
      showTransfer?: boolean;
      expanded?: boolean;
      actionSize?: 'sm';
      showAvatar?: boolean;
      hideIntersectionActions?: boolean;
    }>(),
    {
      showExpand: false,
      showTransfer: false,
      expanded: false,
      actionSize: undefined,
      showAvatar: false,
      hideIntersectionActions: false,
    },
  );

  const emit = defineEmits<{
    (event: 'on-expand'): void;
    (event: 'on-collapse'): void;
    (event: 'on-transfer', call: Call): void;
  }>();

  /**
   * Emits an 'on-expand' event.
   * This function is typically used to notify a parent component that an expansion action has been triggered.
   * @returns {void}
   */
  const expand = (): void => {
    emit('on-expand');
  };

  /**
   * Emits an 'on-collapse' event.
   * This function is typically used to notify a parent component that an expansion action has been triggered.
   * @returns {void}
   */
  const collapse = (): void => {
    emit('on-collapse');
  };

  /**
   * Emits an 'on-transfer' event.
   * This function notifies a parent component that the user has initiated a transfer action.
   * @param {Call} call - The call to transfer.
   * @returns {void}
   */
  const transfer = (call: Call): void => {
    emit('on-transfer', call);
  };
</script>

<template>
  <div class="bv-call-details bg-base-700 relative flex h-full min-h-fit w-full items-center justify-center">
    <template v-if="showExpand">
      <div
        class="expand-icon bg-base-900 hover:bg-base-900/80 absolute end-3 top-3 z-10 cursor-pointer rounded-sm p-1"
        :class="{ expanded }"
        @click="expanded ? collapse() : expand()"
      >
        <img
          class="h-4 w-4"
          :src="expanded ? CollapseIcon : ExpandIcon"
        />
      </div>
    </template>
    <template v-if="activeCall">
      <audio-visualizer
        :call="activeCall"
        class="absolute inset-0 bg-linear-to-b from-white/20 to-white/0"
      />

      <div class="bv-call-details-content relative flex w-full max-w-96 flex-col gap-3 p-3 text-white">
        <bv-previous-call
          :active-call
          :call="previousCall"
          :hide-intersection-actions
          :show-expand
        />

        <bv-active-call
          :action-size
          :call="activeCall"
          :hide-intersection-actions
          :previous-call="previousCall"
          :show-avatar
          :show-transfer
          @on-transfer="transfer(activeCall)"
        />
      </div>
      <div
        v-if="hideIntersectionActions && !!previousCall && activeCall?.status === 'ringing'"
        class="absolute inset-0 z-20 backdrop-blur-[3px]"
      />
    </template>
    <p
      v-else
      class="flex h-full w-full grow items-center justify-center text-xl leading-5 font-medium text-white"
    >
      {{ trans('voip.calls.no_active_calls_trans') }}
    </p>
  </div>
</template>
